import React, { useEffect, useState } from "react";
import {
  BookOpen,
  Briefcase,
  Landmark,
  Plus,
  Save,
  Check,
  CircleCheck,
  CircleMinus,
  Wrench,
  Edit2,
  Lock,
  Search,
} from "lucide-react";
import CapaImpactTypesPanel from "./CapaImpactTypesPanel";
import ApprovalDot from "../common/ApprovalDot";
import WorkflowModal from "../common/WorkflowModal";
import applicableLawsService, {
  ApplicableLaw,
  LAW_TYPE_OPTIONS,
  lawProvisionsService,
  LawProvision,
} from "../../services/applicableLawsService";
import jurisdictionsService, {
  Jurisdiction,
} from "../../services/jurisdictionsService";
import thirdPartiesService, {
  ThirdParty,
  PARTY_TYPE_OPTIONS,
} from "../../services/thirdPartiesService";
import {
  governanceContractsService,
  GovernanceContract,
} from "../../services/governanceService";
import FormControl from "../common/FormControl";
import Modal from "../common/Modal";
import Button from "../common/Button";
import StatusSwitch from "../common/StatusSwitch";
import Pagination from "../common/Pagination";
import Toast, { ToastMessage } from "../common/Toast";
import { confirmWarning, confirmActive } from "../common/confirm";
import extractApiError from "../../utils/apiError";
import NoDataFound from "../common/NoDataFound";
import Skeleton from "../common/Skeleton";
import { useFieldValidation } from "../../validation/useFieldValidation";
import { ValidatedInput, ValidatedTextarea } from "../../validation/ValidatedFields";
import { isUniqueLocal } from "../../validation/uniqueness";
import { usePermission, NOT_AUTHORISED_MSG } from "../../contexts/PermissionContext";

type SubTab = "laws" | "contracts" | "jurisdictions" | "capa_impact_types";
type FormType = "law" | "provision" | "entity" | "contract" | "jurisdiction";

const JURISDICTION_TYPE_OPTIONS = [
  { value: "Judicial", label: "Judicial" },
  { value: "Regulatory", label: "Regulatory" },
  { value: "Administrative", label: "Administrative" },
];

const MASTER_FORM_TITLE: Record<FormType, string> = {
  law: "Create Law",
  provision: "Create Provision",
  entity: "Create Entity",
  contract: "Create Contract",
  jurisdiction: "Create Jurisdiction",
};

const MASTER_FORM_LABEL: Record<FormType, string> = {
  law: "Law",
  provision: "Provision",
  entity: "Entity",
  contract: "Contract",
  jurisdiction: "Jurisdiction",
};

const SECTIONS: { key: SubTab; label: string; icon: React.ElementType }[] = [
  { key: "laws", label: "Law Library", icon: BookOpen },
  { key: "contracts", label: "Parties", icon: Briefcase },
  { key: "jurisdictions", label: "Jurisdictions", icon: Landmark },
  { key: "capa_impact_types", label: "CAPA Impact Types", icon: Wrench },
];

const TAB_ADD_TYPE: Record<SubTab, FormType> = {
  laws: "law",
  contracts: "entity",
  jurisdictions: "jurisdiction",
  capa_impact_types: "jurisdiction", // placeholder; CAPA panel handles its own add
};

interface GovernanceMastersProps {
  /** When supplied, the internal left-rail sub-nav is hidden and the parent
      drives which sub-master is displayed. Used by OrgMasters so the three
      governance masters appear as flat leaves in the consolidated rail
      instead of nested behind a single "Governance Masters" entry. */
  subTab?: SubTab;
}

const GovernanceMasters: React.FC<GovernanceMastersProps> = ({ subTab: subTabProp }) => {
  const controlled = subTabProp !== undefined;
  const [internalSubTab, setInternalSubTab] = useState<SubTab>("laws");
  const masterSubTab: SubTab = controlled ? (subTabProp as SubTab) : internalSubTab;
  const setMasterSubTab = (t: SubTab) => { if (!controlled) setInternalSubTab(t); };
  const [selectedParentId, setSelectedParentId] = useState<number | null>(null);
  const [showMasterModal, setShowMasterModal] = useState(false);
  const [masterFormType, setMasterFormType] = useState<FormType>("law");
  const [masterForm, setMasterForm] = useState<any>({});
  const fieldVh = useFieldValidation();
  const [editingId, setEditingId] = useState<number | null>(null);
  // Jurisdictions + Parties maker-checker: view-first edit lock + workflow modal state.
  const [isFieldEditMode, setIsFieldEditMode] = useState(false);
  const [editingJurWf, setEditingJurWf] = useState<string | null>(null);
  const [jurWfOpen, setJurWfOpen] = useState<Jurisdiction | null>(null);
  const [editingPartyWf, setEditingPartyWf] = useState<string | null>(null);
  const [partyWfOpen, setPartyWfOpen] = useState<ThirdParty | null>(null);
  const [editingLawWf, setEditingLawWf] = useState<string | null>(null);
  const [lawWfOpen, setLawWfOpen] = useState<ApplicableLaw | null>(null);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toggling, setToggling] = useState(false);

  // MIXED FORM — each sub-path is gated to its own backend activity:
  //  - Contracts/Agreements (form type 'contract') → GOV_AGREEMENTS (unchanged)
  //  - Law Library (law + provision), Jurisdictions, Parties (entity) → GOV_MASTERS
  const { allowed: canModifyAgreements, loading: permLoadingAgreements } =
    usePermission('GOV_AGREEMENTS', 'view+modify');
  const { allowed: canModifyMasters, loading: permLoadingMasters } =
    usePermission('GOV_MASTERS', 'view+modify');
  const { allowed: canViewMasters, loading: permLoadingView } = usePermission('GOV_MASTERS', 'view');
  // Note: approve/reject/reopen gating is resolved by the WF engine inside
  // <WorkflowModal> (it returns per-action permissions), so no canApprove here.

  // Required permission for a given master form type. 'contract' stays on
  // GOV_AGREEMENTS; every other master sub-form is gated on GOV_MASTERS.
  const permForType = (type: FormType): { allowed: boolean; loading: boolean } =>
    type === 'contract'
      ? { allowed: canModifyAgreements, loading: permLoadingAgreements }
      : { allowed: canModifyMasters, loading: permLoadingMasters };

  // Gate driven by the ACTIVE form type (the modal submit is shared across types).
  const activePerm = permForType(masterFormType);
  const gateThis = true; // every master form type is now activity-gated
  const allowedForType = activePerm.allowed;
  const permLoadingForType = activePerm.loading;

  // Convenience flags for the GOV_MASTERS-gated list controls (Parties / Jurisdictions / Laws).
  const canModify = canModifyMasters;
  const permLoading = permLoadingMasters;

  const [laws, setLaws] = useState<ApplicableLaw[]>([]);
  // Approved + active laws only — for the Enactment picker in the Jurisdiction form.
  const [enactmentLaws, setEnactmentLaws] = useState<ApplicableLaw[]>([]);
  const [provisions, setProvisions] = useState<LawProvision[]>([]);
  // Law edit modal: Basic Details tab vs. Provisions tab
  const [lawModalTab, setLawModalTab] = useState<"basic" | "provisions">("basic");
  // Provision modal — stacks ABOVE the Law modal (own state so the Law modal stays open)
  const [showProvisionModal, setShowProvisionModal] = useState(false);
  const [provisionEditingId, setProvisionEditingId] = useState<number | null>(null);
  const [provisionForm, setProvisionForm] = useState<any>({});
  const [provisionFieldEdit, setProvisionFieldEdit] = useState(false);
  const [provisionSaving, setProvisionSaving] = useState(false);
  const provisionVh = useFieldValidation();
  const [jurisdictions, setJurisdictions] = useState<Jurisdiction[]>([]);
  const [thirdParties, setThirdParties] = useState<ThirdParty[]>([]);
  const [contracts, setContracts] = useState<GovernanceContract[]>([]);

  const [jurPage, setJurPage] = useState(1);
  const [jurPageSize, setJurPageSize] = useState(10);
  const [jurStatusFilter, setJurStatusFilter] = useState<string>("1");

  const [partyPage, setPartyPage] = useState(1);
  const [partyPageSize, setPartyPageSize] = useState(10);
  const [partyStatusFilter, setPartyStatusFilter] = useState<string>("1");

  // Law Library filters
  const [lawStatusFilter, setLawStatusFilter] = useState<string>("1");
  const [lawSearch, setLawSearch] = useState("");
  const [lawPage, setLawPage] = useState(1);
  const [lawPageSize, setLawPageSize] = useState(10);

  const loadLaws = async () => {
    try {
      setLaws(await applicableLawsService.getAll({ status: 'ALL' } as any));
    } catch (e: any) {
      setToast({ type: "error", message: e?.message || "Failed to load laws" });
    }
  };
  const loadEnactmentLaws = async () => {
    try {
      setEnactmentLaws(await applicableLawsService.getDropdown());
    } catch { /* enactment picker stays empty on failure */ }
  };
  const loadJurisdictions = async () => {
    try {
      setJurisdictions(await jurisdictionsService.getAll());
    } catch (e: any) {
      setToast({
        type: "error",
        message: e?.message || "Failed to load jurisdictions",
      });
    }
  };
  const loadProvisions = async (lawId: number) => {
    try {
      setProvisions(await lawProvisionsService.listForLaw(lawId));
    } catch (e: any) {
      setToast({
        type: "error",
        message: e?.message || "Failed to load provisions",
      });
    }
  };
  const loadThirdParties = async () => {
    try {
      setThirdParties(await thirdPartiesService.getAll({ status: 'ALL' }));
    } catch (e: any) {
      setToast({
        type: "error",
        message: e?.message || "Failed to load third parties",
      });
    }
  };
  const loadContracts = async () => {
    try {
      setContracts(await governanceContractsService.getAll({ limit: 200, status: 'ALL' } as any));
    } catch (e: any) {
      setToast({
        type: "error",
        message: e?.message || "Failed to load contracts",
      });
    }
  };

  useEffect(() => {
    if (masterSubTab === "laws") loadLaws();
    if (masterSubTab === "jurisdictions") {
      loadJurisdictions();
      loadEnactmentLaws();
    }
    if (masterSubTab === "contracts") {
      loadThirdParties();
      loadContracts();
    }
  }, [masterSubTab]);

  useEffect(() => {
    if (masterSubTab === "laws" && selectedParentId) {
      loadProvisions(selectedParentId);
    } else {
      setProvisions([]);
    }
  }, [masterSubTab, selectedParentId]);

  useEffect(() => {
    setJurPage(1);
  }, [masterSubTab, jurPageSize, jurStatusFilter]);

  useEffect(() => {
    setPartyPage(1);
  }, [masterSubTab, partyPageSize, partyStatusFilter]);

  useEffect(() => {
    setLawPage(1);
  }, [masterSubTab, lawPageSize, lawStatusFilter]);

  useEffect(() => {
    if (showMasterModal) {
      setSubmitting(false);
      setToggling(false);
      setIsFieldEditMode(false);
    }
  }, [showMasterModal, editingId]);

  const openMasterModal = (type: FormType, parentId: number | null = null) => {
    setMasterFormType(type);
    setEditingId(null);
    setMasterForm(parentId ? { parentId } : {});
    fieldVh.reset();
    setShowMasterModal(true);
  };

  const openEditModal = (
    type: FormType,
    record: any,
    parentId: number | null = null,
  ) => {
    setMasterFormType(type);
    setEditingId(record.id);
    setIsFieldEditMode(false); // open read-only — "Edit" unlocks the fields
    setEditingJurWf(type === "jurisdiction" ? (record.workflow_state || "draft") : null);
    setEditingPartyWf(type === "entity" ? (record.workflow_state || "draft") : null);
    setEditingLawWf(type === "law" ? (record.workflow_state || "draft") : null);
    const isActive =
      record.is_active === true || record.is_active === 1 ? 1 : 0;
    if (type === "law") {
      setMasterForm({
        code: record.code,
        law_name: record.law_name,
        regulator_name: record.regulator_name,
        law_type: record.law_type,
        is_active: isActive,
      });
    } else if (type === "jurisdiction") {
      setMasterForm({
        code: record.code,
        name: record.name,
        type: record.type,
        enactment_law_id: record.enactment_law_id,
        location: record.location,
        is_active: isActive,
      });
    } else if (type === "provision") {
      setMasterForm({
        parentId,
        section_reference: record.section_reference,
        title: record.title,
        description: record.description,
        penalty_description: record.penalty_description,
        is_active: isActive,
      });
    } else if (type === "entity") {
      setMasterForm({
        code: record.code,
        name: record.name,
        party_type: record.party_type,
        tax_id: record.tax_id,
        is_active: isActive,
      });
    } else if (type === "contract") {
      setMasterForm({
        parentId,
        title: record.title,
        contract_value: record.contract_value,
        is_active: isActive,
      });
    }
    fieldVh.reset();
    setShowMasterModal(true);
  };

  // ── Provision modal (stacked above the Law modal) ───────────────────────────
  const openProvisionCreate = () => {
    setProvisionEditingId(null);
    setProvisionForm({});
    setProvisionFieldEdit(true); // create opens editable
    provisionVh.reset();
    setShowProvisionModal(true);
  };

  const openProvisionEdit = (p: LawProvision) => {
    setProvisionEditingId(p.id);
    setProvisionForm({
      section_reference: p.section_reference,
      title: p.title,
      description: (p as any).description,
      penalty_description: p.penalty_description,
      is_active: Number((p as any).is_active) === 1 ? 1 : 0,
    });
    setProvisionFieldEdit(false); // view-first; "Edit" unlocks
    provisionVh.reset();
    setShowProvisionModal(true);
  };

  const saveProvision = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canModify) {
      setToast({ type: "error", message: NOT_AUTHORISED_MSG });
      return;
    }
    const formatOk = provisionVh.validateAll([
      { name: "section_reference", code: 109, value: provisionForm.section_reference || "" },
      { name: "title", code: 109, value: provisionForm.title || "" },
      { name: "description", code: 110, value: provisionForm.description || "" },
      { name: "penalty_description", code: 110, value: provisionForm.penalty_description || "" },
    ]);
    if (
      !(provisionForm.title || "").trim() &&
      !(provisionForm.section_reference || "").trim()
    ) {
      provisionVh.setError("title", "Title or Section Reference is required");
      return;
    }
    if (!formatOk) return;

    setProvisionSaving(true);
    const isEdit = provisionEditingId !== null;
    try {
      if (isEdit) {
        await lawProvisionsService.update(provisionEditingId!, {
          section_reference: provisionForm.section_reference,
          title: provisionForm.title,
          description: provisionForm.description,
          penalty_description: provisionForm.penalty_description,
        });
      } else {
        await lawProvisionsService.create({
          law_id: editingId ?? undefined,
          section_reference: provisionForm.section_reference,
          title: provisionForm.title,
          description: provisionForm.description,
          penalty_description: provisionForm.penalty_description,
        });
      }
      if (selectedParentId) await loadProvisions(selectedParentId);
      setToast({ type: "success", message: isEdit ? "Provision updated" : "Provision created" });
      setShowProvisionModal(false);
    } catch (err: any) {
      setToast({ type: "error", message: extractApiError(err, "Save failed") });
    } finally {
      setProvisionSaving(false);
    }
  };

  const toggleProvisionActive = async () => {
    if (provisionEditingId === null) return;
    const currentlyActive = provisionForm.is_active === 1 || provisionForm.is_active === true;
    const next = currentlyActive ? 0 : 1;
    const confirmFn = currentlyActive ? confirmWarning : confirmActive;
    const ok = await confirmFn({
      title: next ? "Reactivate this provision?" : "Inactivate this provision?",
      text: next ? "It will appear in active lists again." : "It will be hidden from active lists.",
      confirmText: next ? "Yes, Reactivate" : "Yes, Inactivate",
    });
    if (!ok) return;
    setProvisionSaving(true);
    try {
      await lawProvisionsService.update(provisionEditingId, { is_active: next });
      if (selectedParentId) await loadProvisions(selectedParentId);
      setProvisionForm({ ...provisionForm, is_active: next });
      setToast({ type: next ? "success" : "warning", message: next ? "Provision reactivated." : "Provision inactivated." });
    } catch (err: any) {
      setToast({ type: "error", message: extractApiError(err, "Failed to update status") });
    } finally {
      setProvisionSaving(false);
    }
  };

  const getEditingDisplayName = (): string => {
    if (!editingId) return "";
    if (masterFormType === "law") return masterForm.law_name || "";
    if (masterFormType === "jurisdiction") return masterForm.name || "";
    if (masterFormType === "provision")
      return masterForm.title || masterForm.section_reference || "";
    if (masterFormType === "entity") return masterForm.name || "";
    if (masterFormType === "contract") return masterForm.title || "";
    return "";
  };

  const saveMaster = async (e: React.FormEvent) => {
    e.preventDefault();

    // Each master sub-path is activity-gated: 'contract' → GOV_AGREEMENTS,
    // all other master form types → GOV_MASTERS. Block submit if not allowed.
    if (!permForType(masterFormType).allowed) {
      setToast({ type: "error", message: NOT_AUTHORISED_MSG });
      return;
    }

    // Format-level validation (engine 109/110) per form type.
    let formatOk = true;
    if (masterFormType === "law") {
      formatOk = fieldVh.validateAll([
        { name: "law_name", code: 109, value: masterForm.law_name || "", required: true },
        { name: "regulator_name", code: 109, value: masterForm.regulator_name || "" },
      ]);
    } else if (masterFormType === "jurisdiction") {
      formatOk = fieldVh.validateAll([
        { name: "name", code: 109, value: masterForm.name || "", required: true },
        { name: "location", code: 109, value: masterForm.location || "" },
      ]);
    } else if (masterFormType === "entity") {
      formatOk = fieldVh.validateAll([
        { name: "name", code: 109, value: masterForm.name || "", required: true },
      ]);
    } else if (masterFormType === "contract") {
      formatOk = fieldVh.validateAll([
        { name: "title", code: 109, value: masterForm.title || "", required: true },
      ]);
    } else if (masterFormType === "provision") {
      // Title is required only when no Section Reference is given (title OR section rule).
      formatOk = fieldVh.validateAll([
        { name: "section_reference", code: 109, value: masterForm.section_reference || "" },
        { name: "title", code: 109, value: masterForm.title || "" },
        { name: "description", code: 110, value: masterForm.description || "" },
        { name: "penalty_description", code: 110, value: masterForm.penalty_description || "" },
      ]);
      if (
        !(masterForm.title || "").trim() &&
        !(masterForm.section_reference || "").trim()
      ) {
        fieldVh.setError("title", "Title or Section Reference is required");
        formatOk = false;
      }
    }
    if (!formatOk) return;

    // Uniqueness (entity-scoped) — case-insensitive local pre-check.
    if (masterFormType === "law" &&
        !isUniqueLocal(925, masterForm.law_name || "", laws, editingId ?? undefined)) {
      fieldVh.setError("law_name", "Law with this name already exists");
      return;
    }
    if (masterFormType === "jurisdiction" &&
        !isUniqueLocal(926, masterForm.name || "", jurisdictions, editingId ?? undefined)) {
      fieldVh.setError("name", "Authority Name already exists");
      return;
    }
    if (masterFormType === "entity" &&
        !isUniqueLocal(927, masterForm.name || "", thirdParties, editingId ?? undefined)) {
      fieldVh.setError("name", "Entity with this name already exists");
      return;
    }

    setSubmitting(true);
    const isEdit = editingId !== null;
    try {
      if (masterFormType === "law") {
        const payload = {
          law_name: masterForm.law_name,
          regulator_name: masterForm.regulator_name || masterForm.law_name,
          law_type: masterForm.law_type || "OTHER",
        } as any;
        if (isEdit) await applicableLawsService.update(editingId!, payload);
        else await applicableLawsService.create(payload);
        await loadLaws();
      } else if (masterFormType === "jurisdiction") {
        const payload = {
          name: masterForm.name,
          type: masterForm.type || "Judicial",
          enactment_law_id: masterForm.enactment_law_id
            ? Number(masterForm.enactment_law_id)
            : null,
          location: masterForm.location,
        };
        if (isEdit) await jurisdictionsService.update(editingId!, payload);
        else await jurisdictionsService.create(payload);
        await loadJurisdictions();
      } else if (masterFormType === "provision") {
        if (isEdit) {
          await lawProvisionsService.update(editingId!, {
            section_reference: masterForm.section_reference,
            title: masterForm.title,
            description: masterForm.description,
            penalty_description: masterForm.penalty_description,
          });
        } else {
          await lawProvisionsService.create({
            law_id: masterForm.parentId,
            section_reference: masterForm.section_reference,
            title: masterForm.title,
            description: masterForm.description,
            penalty_description: masterForm.penalty_description,
          });
        }
        if (selectedParentId) await loadProvisions(selectedParentId);
      } else if (masterFormType === "entity") {
        const payload = {
          name: masterForm.name,
          party_type: masterForm.party_type || "VENDOR",
          tax_id: masterForm.tax_id,
        };
        if (isEdit) await thirdPartiesService.update(editingId!, payload);
        else await thirdPartiesService.create(payload);
        await loadThirdParties();
      } else if (masterFormType === "contract") {
        const party = thirdParties.find((p) => p.id === masterForm.parentId);
        if (isEdit) {
          await governanceContractsService.update(editingId!, {
            title: masterForm.title,
            contract_value: masterForm.contract_value,
          } as any);
        } else {
          await governanceContractsService.create({
            title: masterForm.title,
            party_name: party?.name || "",
            contract_type:
              party?.party_type === "CUSTOMER"
                ? "CUSTOMER"
                : party?.party_type === "AGENCY"
                  ? "AGENCY"
                  : "VENDOR",
            contract_status: "DRAFT",
            contract_value: masterForm.contract_value,
          } as any);
        }
        await loadContracts();
      }
      setToast({
        type: "success",
        message: isEdit
          ? `${MASTER_FORM_LABEL[masterFormType]} updated`
          : `${MASTER_FORM_LABEL[masterFormType]} created`,
      });
      setShowMasterModal(false);
      setMasterForm({});
      setEditingId(null);
    } catch (e: any) {
      const msg = extractApiError(e, "Save failed");
      const nameField =
        masterFormType === "law"
          ? "law_name"
          : masterFormType === "jurisdiction" || masterFormType === "entity"
            ? "name"
            : "title";
      if (/exist|duplicate|unique/i.test(msg)) {
        fieldVh.setError(nameField, msg);
      } else {
        setToast({ type: "error", message: msg });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async () => {
    if (!editingId) return;
    const currentlyActive =
      masterForm.is_active === 1 || masterForm.is_active === true;
    const next = currentlyActive ? 0 : 1;
    const confirmFn = currentlyActive ? confirmWarning : confirmActive;
    const ok = await confirmFn({
      title: next
        ? `Reactivate this ${MASTER_FORM_LABEL[masterFormType].toLowerCase()}?`
        : `Inactivate this ${MASTER_FORM_LABEL[masterFormType].toLowerCase()}?`,
      text: next
        ? "It will appear in active lists again."
        : "It will be hidden from active lists.",
      confirmText: next ? "Yes, Reactivate" : "Yes, Inactivate",
    });
    if (!ok) return;
    setToggling(true);
    try {
      if (masterFormType === "law") {
        await applicableLawsService.update(editingId, {
          is_active: Boolean(next),
        });
        await loadLaws();
      } else if (masterFormType === "jurisdiction") {
        await jurisdictionsService.update(editingId, { is_active: next });
        await loadJurisdictions();
      } else if (masterFormType === "provision") {
        await lawProvisionsService.update(editingId, { is_active: next });
        if (selectedParentId) await loadProvisions(selectedParentId);
      } else if (masterFormType === "entity") {
        await thirdPartiesService.update(editingId, { is_active: next });
        await loadThirdParties();
      } else if (masterFormType === "contract") {
        await governanceContractsService.update(editingId, {
          is_active: next,
        } as any);
        await loadContracts();
      }
      setMasterForm((prev: any) => ({ ...prev, is_active: next }));
      setShowMasterModal(false);
      setToast({
        type: next ? "success" : "warning",
        message: next
          ? `${MASTER_FORM_LABEL[masterFormType]} reactivated.`
          : `${MASTER_FORM_LABEL[masterFormType]} inactivated.`,
      });
    } catch (e: any) {
      setToast({
        type: "error",
        message: extractApiError(e, "Failed to update status"),
      });
    } finally {
      setToggling(false);
    }
  };

  const lawOptions = enactmentLaws.map((l) => ({
    value: String(l.id),
    label: l.law_name,
  }));
  const filteredJurisdictions = [...jurisdictions]
    .filter((j) => {
      if (jurStatusFilter === "ALL") return true;
      const active = Number((j as any).is_active) === 1 ? 1 : 0;
      return String(active) === jurStatusFilter;
    })
    .sort((a, b) => (b.id || 0) - (a.id || 0));
  const paginatedJurisdictions = filteredJurisdictions.slice(
    (jurPage - 1) * jurPageSize,
    jurPage * jurPageSize,
  );

  const filteredParties = [...thirdParties]
    .filter((p) => {
      if (partyStatusFilter === "ALL") return true;
      const active = Number((p as any).is_active) === 1 ? 1 : 0;
      return String(active) === partyStatusFilter;
    })
    .sort((a, b) => (b.id || 0) - (a.id || 0));
  const paginatedParties = filteredParties.slice(
    (partyPage - 1) * partyPageSize,
    partyPage * partyPageSize,
  );

  const filteredLaws = [...laws]
    .filter((l) => {
      if (lawStatusFilter === "ALL") return true;
      const active = Number((l as any).is_active) === 1 ? 1 : 0;
      return String(active) === lawStatusFilter;
    })
    .filter((l) => {
      const q = lawSearch.trim().toLowerCase();
      if (!q) return true;
      return (
        (l.law_name || "").toLowerCase().includes(q) ||
        ((l as any).code || "").toLowerCase().includes(q) ||
        (l.jurisdiction || "").toLowerCase().includes(q) ||
        (LAW_TYPE_OPTIONS.find((o) => o.value === l.law_type)?.label || l.law_type || "")
          .toLowerCase()
          .includes(q)
      );
    })
    .sort((a, b) => (b.id || 0) - (a.id || 0));
  const paginatedLaws = filteredLaws.slice(
    (lawPage - 1) * lawPageSize,
    lawPage * lawPageSize,
  );

  // ── Jurisdictions + Parties maker-checker derived flags + transitions ──────
  const jurIsApproved =
    masterFormType === "jurisdiction" && editingId !== null && editingJurWf === "approved";
  const jurFieldsDisabled =
    masterFormType === "jurisdiction" && editingId !== null &&
    (jurIsApproved || !isFieldEditMode);
  const entityIsApproved =
    masterFormType === "entity" && editingId !== null && editingPartyWf === "approved";
  const entityFieldsDisabled =
    masterFormType === "entity" && editingId !== null &&
    (entityIsApproved || !isFieldEditMode);
  const lawIsApproved =
    masterFormType === "law" && editingId !== null && editingLawWf === "approved";
  const lawFieldsDisabled =
    masterFormType === "law" && editingId !== null &&
    (lawIsApproved || !isFieldEditMode);
  const provisionFieldsDisabled =
    masterFormType === "provision" && editingId !== null && !isFieldEditMode;
  // Combined flags driving the shared edit-modal footer + Inactive toggle.
  const mcForm = masterFormType === "jurisdiction" || masterFormType === "entity" || masterFormType === "law";
  const mcApproved = jurIsApproved || entityIsApproved || lawIsApproved;

  // Workflow (submit/approve/reject/reopen) for jurisdictions, third parties
  // and applicable laws is now driven end-to-end by the unified <WorkflowModal>
  // via the /api/v1/wf engine.

  // Not-authorised gate for the GOV_MASTERS master sub-tabs (· view).
  if ((masterSubTab === "laws" || masterSubTab === "jurisdictions" || masterSubTab === "contracts") && !permLoadingView && !canViewMasters) {
    return (
      <div className="flex h-full overflow-hidden">
        <div className="netbody-card side-content-card flex flex-col flex-1 min-w-0">
          <NoDataFound title="Not authorised" message={NOT_AUTHORISED_MSG} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full overflow-hidden">
      {/* ── Left nav card ────────────────────────────────────────────
          Hidden when the parent component controls subTab — typically
          OrgMasters, which surfaces the three governance masters
          (laws / contracts / jurisdictions) as flat entries in its own
          consolidated left rail. */}
      {!controlled && (
        <div className="netbody-card side-nav-card">
          <div className="side-nav-card-heading">Masters</div>
          <nav aria-label="Masters navigation">
            {SECTIONS.map(({ key, label, icon: Icon }) => (
              <button
                data-testid={`NAV_governance_master_${key}`}
                key={key}
                onClick={() => {
                  setMasterSubTab(key);
                  setSelectedParentId(null);
                }}
                className={`side-panel-menu-item${masterSubTab === key ? " side-panel-menu-item--active" : ""}`}
              >
                <Icon size={14} />
                {label}
              </button>
            ))}
          </nav>
        </div>
      )}

      {/* ── Right side: CAPA panel takes over fully OR existing right card ── */}
      {masterSubTab === "capa_impact_types" ? (
        <div className="flex-1 min-w-0">
          <CapaImpactTypesPanel />
        </div>
      ) : (
      <div className="netbody-card side-content-card flex flex-col flex-1 min-w-0">
        {/* Toolbar */}
        <div className="netbody-header">
          {masterSubTab === "laws" && (
            <StatusSwitch
              value={lawStatusFilter === "0" ? "INACTIVE" : "ACTIVE"}
              onChange={(next) => setLawStatusFilter(next === "INACTIVE" ? "0" : "1")}
              noun="laws"
              testId="SEL_governance_master_law_status_filter"
            />
          )}
          {masterSubTab === "jurisdictions" && (
            <StatusSwitch
              value={jurStatusFilter === '0' ? 'INACTIVE' : 'ACTIVE'}
              onChange={(next) => setJurStatusFilter(next === 'INACTIVE' ? '0' : '1')}
              noun="jurisdictions"
              testId="SEL_governance_master_jur_status_filter"
            />
          )}
          {masterSubTab === "contracts" && (
            <StatusSwitch
              value={partyStatusFilter === '0' ? 'INACTIVE' : 'ACTIVE'}
              onChange={(next) => setPartyStatusFilter(next === 'INACTIVE' ? '0' : '1')}
              noun="parties"
              testId="SEL_governance_master_party_status_filter"
            />
          )}
          <div className="flex items-center gap-2 ml-auto">
            {masterSubTab === "laws" && (
              <>
                <FormControl.Input
                  data-testid="INP_governance_master_law_search"
                  type="text"
                  placeholder="Search applicable laws…"
                  value={lawSearch}
                  onChange={(e) => { setLawSearch(e.target.value); setLawPage(1); }}
                  leadingIcon={<Search size={14} />}
                  className="filter-search"
                />
                <Button
                  data-testid="BTN_governance_master_law_create"
                  variant="fill"
                  icon={Plus}
                  onClick={() => openMasterModal("law")}
                  disabled={permLoading || !canModify}
                  title={!canModify ? NOT_AUTHORISED_MSG : undefined}
                >
                  Create
                </Button>
              </>
            )}
            {masterSubTab === "contracts" && (
              <Button
                data-testid="BTN_governance_master_entity_create"
                variant="fill"
                icon={Plus}
                onClick={() => openMasterModal("entity")}
                disabled={permLoading || !canModify}
                title={!canModify ? NOT_AUTHORISED_MSG : undefined}
              >
                Create
              </Button>
            )}
            {masterSubTab !== "contracts" && masterSubTab !== "laws" && (
              <Button
                data-testid="BTN_governance_master_create"
                variant="fill"
                icon={Plus}
                onClick={() => openMasterModal(TAB_ADD_TYPE[masterSubTab])}
                disabled={permLoading || !canModify}
                title={!canModify ? NOT_AUTHORISED_MSG : undefined}
              >
                Create
              </Button>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="netbody-content">
          {/* ── Laws section ─────────────────────────────────────── */}
          {masterSubTab === "laws" && (
            <div className="scroll-container flex-1 overflow-hidden">
              <div className="table-list">
                <div className="table-list-scroll">
                  <table className="table-list-table">
                        <thead>
                          <tr>
                            <th className="table-list-th table-list-cell--dot" aria-label="Workflow status"></th>
                            <th className="table-list-th table-list-cell--sno">S.No</th>
                            <th className="table-list-th">Code </th>
                            <th className="table-list-th" style={{ width: "50%" }}>Name</th>
                            <th className="table-list-th">Regulator</th>
                            <th className="table-list-th">Type</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredLaws.length === 0 && (
                            <tr>
                              <td colSpan={6} className="table-list-cell">
                                <NoDataFound
                                  title={lawSearch || lawStatusFilter === "0" ? "No Matching Records" : "No Laws Found"}
                                  message={lawSearch || lawStatusFilter === "0" ? "No records match your filters." : 'Click "Create" to add one.'}
                                />
                              </td>
                            </tr>
                          )}
                          {paginatedLaws.map((law, idx) => {
                            const lawActive = Number((law as any).is_active) === 1;
                            return (
                              <tr
                                data-testid={`BTN_governance_master_law_row_${law.id}_select`}
                                key={law.id}
                                className={`table-list-row${lawActive ? "" : " is-inactive"}`}
                                onClick={() => {
                                  if (permLoading || !canModify) {
                                    setToast({ type: "error", message: NOT_AUTHORISED_MSG });
                                    return;
                                  }
                                  setSelectedParentId(law.id);
                                  setLawModalTab("provisions");
                                  openEditModal("law", law);
                                }}
                                style={{ cursor: "pointer" }}
                              >
                                <td
                                  className="table-list-cell table-list-cell--dot"
                                  onClick={(e) => { e.stopPropagation(); setLawWfOpen(law); }}
                                  title="Workflow & history"
                                  data-testid={`BTN_governance_master_law_row_${law.id}_wf`}
                                >
                                  <ApprovalDot
                                    state={((law as any).workflow_state || "draft") as any}
                                    onClick={(e) => { e.stopPropagation(); setLawWfOpen(law); }}
                                    title="Workflow & history"
                                  />
                                </td>
                                <td className="table-list-cell table-list-cell--sno">
                                  {(lawPage - 1) * lawPageSize + idx + 1}
                                </td>
                                <td className="table-list-cell" >
                                  <span className="cell-code">
                                    {(law as any).code || "—"}
                                  </span>
                                </td>
                                <td className="table-list-cell">
                                  <span className="table-list-cell-title cell-name-lock">
                                    {(law as any).workflow_state === "approved" && (
                                      <Lock className="lock-icon lock-icon--sm" aria-label="Locked — approved" />
                                    )}
                                    {law.law_name}
                                  </span>
                                </td>
                                <td className="table-list-cell" style={{ color: "#64748b" }}>
                                  {law.regulator_name || "—"}
                                </td>
                                <td className="table-list-cell">
                                  <span className="status-badge sm info">
                                    {LAW_TYPE_OPTIONS.find(
                                      (o) => o.value === law.law_type,
                                    )?.label || law.law_type}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <Pagination
                      page={lawPage}
                      pageSize={lawPageSize}
                      totalRecords={filteredLaws.length}
                      onPageChange={setLawPage}
                      onPageSizeChange={setLawPageSize}
                      pageSizeOptions={[10, 25, 50, 100]}
                      recordLabel="laws"
                    />
                  </div>
                </div>
          )}

          {/* ── Contracts section ────────────────────────────────── */}
          {masterSubTab === "contracts" && (
            <div className="scroll-container">
              <div className="table-list">
                  <div className="table-list-scroll">
                    <table className="table-list-table">
                      <thead>
                        <tr>
                          <th className="table-list-th" style={{ width: 36 }} aria-label="Workflow status"></th>
                          <th className="table-list-th table-list-cell--sno">S.No</th>
                          <th className="table-list-th">Code</th>
                          <th className="table-list-th">Name</th>
                          <th className="table-list-th">Type</th>
                          <th className="table-list-th">Tax ID</th>
                        </tr>
                      </thead>
                      <tbody aria-busy={loading} aria-live="polite">
                        {loading && <Skeleton.TableRows cols={['3xs', '3xs', 'sm', 'md', 'pill', 'sm']} />}
                        {!loading && filteredParties.length === 0 && (
                          <tr>
                            <td colSpan={6} className="table-list-cell">
                              <NoDataFound title="No Parties Found" message='Click "Create" to add one.' />
                            </td>
                          </tr>
                        )}
                        {!loading &&
                          paginatedParties.map((ent, idx) => {
                            const partyActive = Number(ent.is_active) === 1;
                            return (
                              <tr
                                data-testid={`BTN_governance_master_entity_row_${ent.id}_select`}
                                key={ent.id}
                                className={`table-list-row${partyActive ? "" : " is-inactive"}`}
                                onClick={() => openEditModal("entity", ent)}
                                style={{ cursor: "pointer" }}
                              >
                                <td
                                  className="table-list-cell"
                                  style={{ width: 52, paddingLeft: 8, paddingRight: 4, cursor: "pointer" }}
                                  onClick={(e) => { e.stopPropagation(); setPartyWfOpen(ent); }}
                                  title="Workflow & history"
                                  data-testid={`BTN_governance_master_entity_row_${ent.id}_wf`}
                                >
                                  <div style={{ display: "flex", alignItems: "center" }}>
                                    <ApprovalDot
                                      state={((ent as any).workflow_state || "draft") as any}
                                      onClick={(e) => { e.stopPropagation(); setPartyWfOpen(ent); }}
                                      title="Workflow & history"
                                    />
                                  </div>
                                </td>
                                <td className="table-list-cell table-list-cell--sno">
                                  {(partyPage - 1) * partyPageSize + idx + 1}
                                </td>
                                <td className="table-list-cell">
                                  <span className="cell-code">
                                    {(ent as any).code || "—"}
                                  </span>
                                </td>
                                <td className="table-list-cell">
                                  <span className="table-list-cell-title cell-name-lock">
                                    {(ent as any).workflow_state === "approved" && (
                                      <Lock className="lock-icon lock-icon--sm" aria-label="Locked — approved" />
                                    )}
                                    {ent.name}
                                  </span>
                                </td>
                                <td className="table-list-cell">
                                  <span className="status-badge xs info">
                                    {PARTY_TYPE_OPTIONS.find(
                                      (o) => o.value === ent.party_type,
                                    )?.label || ent.party_type}
                                  </span>
                                </td>
                                <td className="table-list-cell" style={{ color: "#94a3b8" }}>
                                  {ent.tax_id || "—"}
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                  <Pagination
                    page={partyPage}
                    pageSize={partyPageSize}
                    totalRecords={filteredParties.length}
                    onPageChange={setPartyPage}
                    onPageSizeChange={setPartyPageSize}
                    recordLabel="parties"
                  />
                </div>
            </div>
          )}

          {/* ── Jurisdictions section ────────────────────────────── */}
          {masterSubTab === "jurisdictions" && (
            <div className="scroll-container">
              <div className="table-list">
                <div className="table-list-scroll">
                  <table className="table-list-table">
                    <thead>
                      <tr>
                        <th className="table-list-th table-list-cell--dot" aria-label="Workflow status"></th>
                        <th className="table-list-th table-list-cell--sno">S.No</th>
                        <th className="table-list-th">Code</th>
                        <th className="table-list-th">Authority Name</th>
                        <th className="table-list-th">Type</th>
                        <th className="table-list-th">Enactment</th>
                        <th className="table-list-th">Location</th>
                      </tr>
                    </thead>
                    <tbody aria-busy={loading} aria-live="polite">
                      {loading && <Skeleton.TableRows cols={['3xs', '3xs', 'sm', 'md', 'pill', 'md', 'sm']} />}
                      {!loading && filteredJurisdictions.length === 0 && (
                        <tr>
                          <td colSpan={7} className="table-list-cell">
                            <NoDataFound title="No Jurisdictions Found" message='Click "Create" to add one.' />
                          </td>
                        </tr>
                      )}
                      {!loading &&
                        paginatedJurisdictions.map((j, idx) => {
                            const isActive = Number((j as any).is_active) === 1;
                            return (
                            <tr
                              data-testid={`BTN_governance_master_jurisdiction_row_${j.id}_open`}
                              key={j.id}
                              className={`table-list-row${isActive ? "" : " is-inactive"}`}
                              onClick={() => openEditModal("jurisdiction", j)}
                              style={{ cursor: "pointer" }}
                            >
                              <td
                                className="table-list-cell table-list-cell--dot"
                                onClick={(e) => { e.stopPropagation(); setJurWfOpen(j); }}
                                title="Workflow & history"
                                data-testid={`BTN_governance_master_jurisdiction_row_${j.id}_wf`}
                              >
                                <ApprovalDot
                                  state={((j as any).workflow_state || "draft") as any}
                                  onClick={(e) => { e.stopPropagation(); setJurWfOpen(j); }}
                                  title="Workflow & history"
                                />
                              </td>
                              <td className="table-list-cell table-list-cell--sno">
                                {(jurPage - 1) * jurPageSize + idx + 1}
                              </td>
                              <td className="table-list-cell">
                                <span className="cell-code">
                                  {(j as any).code || "—"}
                                </span>
                              </td>
                              <td className="table-list-cell">
                                <span className="table-list-cell-title cell-name-lock">
                                  {(j as any).workflow_state === "approved" && (
                                    <Lock className="lock-icon lock-icon--sm" aria-label="Locked — approved" />
                                  )}
                                  {j.name}
                                </span>
                              </td>
                              <td className="table-list-cell">
                                <span className="status-badge xs info">
                                  {j.type}
                                </span>
                              </td>
                              <td className="table-list-cell">
                                {j.enactment_law_name ||
                                  (j as any).enactment ||
                                  "—"}
                              </td>
                              <td className="table-list-cell">{j.location}</td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  page={jurPage}
                  pageSize={jurPageSize}
                  totalRecords={filteredJurisdictions.length}
                  onPageChange={setJurPage}
                  onPageSizeChange={setJurPageSize}
                  recordLabel="jurisdictions"
                />
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      <Toast toast={toast} onClose={() => setToast(null)} />

      {/* ── Master Create Modal ─────────────────────────────────────── */}
      <Modal
        isOpen={showMasterModal}
        onClose={() => setShowMasterModal(false)}
        size={editingId && masterFormType === "law" ? "lg" : "md"}
      >
        <Modal.Header>
          {editingId ? (
            <>
              {mcApproved ? 'Viewing (locked)' : isFieldEditMode ? 'Editing' : 'Viewing'} "
              <span className="item-highlight">{getEditingDisplayName()}</span>"
            </>
          ) : (
            MASTER_FORM_TITLE[masterFormType]
          )}
        </Modal.Header>
        <Modal.Body>
          <form
            data-testid="MOD_governance_master_form"
            id="master-form"
            onSubmit={saveMaster}
            className="flex flex-col gap-y-4"
          >
            {masterFormType === "law" && (
              <>
                {/* Tabs — only when editing an existing law (provisions need a saved law) */}
                {editingId && (
                  <div className="app-modal-tabs">
                    <button
                      type="button"
                      className={`app-modal-tab${lawModalTab === "basic" ? " app-modal-tab--active" : ""}`}
                      onClick={() => setLawModalTab("basic")}
                      data-testid="TAB_governance_master_law_basic"
                    >
                      Basic Details
                    </button>
                    <button
                      type="button"
                      className={`app-modal-tab${lawModalTab === "provisions" ? " app-modal-tab--active" : ""}`}
                      onClick={() => setLawModalTab("provisions")}
                      data-testid="TAB_governance_master_law_provisions"
                    >
                      Provisions
                    </button>
                  </div>
                )}

                {/* ── Basic Details tab ── */}
                {(!editingId || lawModalTab === "basic") && (
                  <>
                    {lawIsApproved && (
                      <div className="approved-banner">
                        <Lock size={14} /> Record is approved and locked. Reopen via the workflow button to edit.
                      </div>
                    )}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                      <FormControl.Input
                        label="Code"
                        type="text"
                        name="code"
                        value={masterForm.code || ""}
                        disabled
                        placeholder="—"
                        data-testid="INP_governance_master_law_code"
                      />
                      <ValidatedInput
                        vh={fieldVh}
                        name="law_name"
                        code={109}
                        required
                        disabled={lawFieldsDisabled}
                        data-testid="INP_governance_master_law_name"
                        label="Law Name"
                        value={masterForm.law_name || ""}
                        onChange={(e) =>
                          setMasterForm({ ...masterForm, law_name: e.target.value })
                        }
                      />
                    </div>
                    <ValidatedInput
                      vh={fieldVh}
                      name="regulator_name"
                      code={109}
                      disabled={lawFieldsDisabled}
                      data-testid="INP_governance_master_law_regulator"
                      label="Regulator"
                      value={masterForm.regulator_name || ""}
                      onChange={(e) =>
                        setMasterForm({
                          ...masterForm,
                          regulator_name: e.target.value,
                        })
                      }
                    />
                    <FormControl.SearchSelect
                      data-testid="SEL_governance_master_law_type"
                      label="Law Type"
                      disabled={lawFieldsDisabled}
                      value={masterForm.law_type || ""}
                      onChange={(value) =>
                        setMasterForm({
                          ...masterForm,
                          law_type: String(value ?? ""),
                        })
                      }
                      options={LAW_TYPE_OPTIONS}
                      placeholder="— Select —"
                    />
                  </>
                )}

                {/* ── Provisions tab ── */}
                {editingId && lawModalTab === "provisions" && (
                  <div className="flex flex-col gap-y-3">
                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                      <Button
                        data-testid="BTN_governance_master_provision_create"
                        variant="fill"
                        icon={Plus}
                        onClick={() => {
                          if (lawIsApproved) {
                            setToast({
                              type: "error",
                              message: "Record is approved and locked. Reopen via the workflow button to edit.",
                            });
                            return;
                          }
                          if (editingId) openProvisionCreate();
                        }}
                        disabled={permLoading || !canModify}
                        aria-disabled={lawIsApproved}
                        title={
                          lawIsApproved
                            ? "Record is approved and locked. Reopen via the workflow button to edit."
                            : !canModify
                              ? NOT_AUTHORISED_MSG
                              : undefined
                        }
                        style={lawIsApproved ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
                      >
                        Create
                      </Button>
                    </div>
                    <div className="table-list">
                      <div className="table-list-scroll">
                        <table className="table-list-table">
                          <thead>
                            <tr>
                              <th className="table-list-th table-list-cell--sno">S.No</th>
                              <th className="table-list-th">Section</th>
                              <th className="table-list-th">Title</th>
                              <th className="table-list-th">Penalty</th>
                            </tr>
                          </thead>
                          <tbody>
                            {loading && <Skeleton.TableRows cols={['3xs', '2xs', 'md', 'md']} />}
                            {!loading && provisions.length === 0 && (
                              <tr>
                                <td colSpan={4} className="table-list-cell">
                                  <NoDataFound
                                    title="No Provisions"
                                    message='No provisions yet. Click "Add Provision" to create one.'
                                  />
                                </td>
                              </tr>
                            )}
                            {!loading && [...provisions]
                              .sort((a, b) => (b.id || 0) - (a.id || 0))
                              .map((p, idx) => {
                                const provActive = Number((p as any).is_active) === 1;
                                return (
                                  <tr
                                    data-testid={`BTN_governance_master_provision_row_${p.id}_open`}
                                    key={p.id}
                                    className={`table-list-row${provActive ? "" : " is-inactive"}`}
                                    title={!canModify ? NOT_AUTHORISED_MSG : undefined}
                                    onClick={() => {
                                      if (permLoading || !canModify) {
                                        setToast({ type: "error", message: NOT_AUTHORISED_MSG });
                                        return;
                                      }
                                      openProvisionEdit(p);
                                    }}
                                    style={{
                                      cursor: (permLoading || !canModify) ? "not-allowed" : "pointer",
                                      opacity: (permLoading || !canModify) ? 0.6 : 1,
                                    }}
                                  >
                                    <td className="table-list-cell table-list-cell--sno">{idx + 1}</td>
                                    <td className="table-list-cell">
                                      <span className="table-list-cell-title">
                                        {p.section_reference || "—"}
                                      </span>
                                    </td>
                                    <td className="table-list-cell">{p.title}</td>
                                    <td className="table-list-cell" style={{ color: "#dc2626", fontSize: "0.75rem" }}>
                                      {p.penalty_description || "—"}
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {masterFormType === "entity" && (
              <>
                {entityIsApproved && (
                  <div className="approved-banner">
                    <Lock size={14} /> Record is approved and locked. Reopen via the workflow button to edit.
                  </div>
                )}
                {editingId && (
                  <FormControl.Input
                    label="Code"
                    type="text"
                    name="code"
                    value={masterForm.code || ""}
                    disabled
                    placeholder="—"
                    data-testid="INP_governance_master_entity_code"
                  />
                )}
                <ValidatedInput
                  vh={fieldVh}
                  name="name"
                  code={109}
                  required
                  disabled={entityFieldsDisabled}
                  data-testid="INP_governance_master_entity_name"
                  label="Name"
                  value={masterForm.name || ""}
                  onChange={(e) =>
                    setMasterForm({ ...masterForm, name: e.target.value })
                  }
                />
                <FormControl.SearchSelect
                  data-testid="SEL_governance_master_entity_party_type"
                  label="Type"
                  disabled={entityFieldsDisabled}
                  value={masterForm.party_type || "VENDOR"}
                  onChange={(value) =>
                    setMasterForm({
                      ...masterForm,
                      party_type: String(value ?? "VENDOR"),
                    })
                  }
                  options={PARTY_TYPE_OPTIONS}
                />
                <FormControl.Input
                  data-testid="INP_governance_master_entity_tax_id"
                  label="Tax ID"
                  disabled={entityFieldsDisabled}
                  value={masterForm.tax_id || ""}
                  onChange={(e) =>
                    setMasterForm({ ...masterForm, tax_id: e.target.value })
                  }
                />
              </>
            )}

            {masterFormType === "contract" && (
              <>
                <ValidatedInput
                  vh={fieldVh}
                  name="title"
                  code={109}
                  required
                  data-testid="INP_governance_master_contract_title"
                  label="Title"
                  value={masterForm.title || ""}
                  onChange={(e) =>
                    setMasterForm({ ...masterForm, title: e.target.value })
                  }
                />
                <FormControl.Input
                  data-testid="INP_governance_master_contract_value"
                  label="Value"
                  value={masterForm.contract_value || ""}
                  onChange={(e) =>
                    setMasterForm({
                      ...masterForm,
                      contract_value: e.target.value,
                    })
                  }
                />
              </>
            )}

            {masterFormType === "jurisdiction" && (
              <>
                {jurIsApproved && (
                  <div className="approved-banner">
                    <Lock size={14} /> Record is approved and locked. Reopen via the workflow button to edit.
                  </div>
                )}
                {editingId && (
                  <FormControl.Input
                    label="Code"
                    type="text"
                    name="code"
                    value={masterForm.code || ""}
                    disabled
                    placeholder="—"
                    data-testid="INP_governance_master_jurisdiction_code"
                  />
                )}
                <ValidatedInput
                  vh={fieldVh}
                  name="name"
                  code={109}
                  required
                  disabled={jurFieldsDisabled}
                  data-testid="INP_governance_master_jurisdiction_name"
                  label="Authority Name"
                  value={masterForm.name || ""}
                  onChange={(e) =>
                    setMasterForm({ ...masterForm, name: e.target.value })
                  }
                />
                <FormControl.SearchSelect
                  data-testid="SEL_governance_master_jurisdiction_type"
                  label="Type"
                  disabled={jurFieldsDisabled}
                  value={masterForm.type || "Judicial"}
                  onChange={(value) =>
                    setMasterForm({
                      ...masterForm,
                      type: String(value ?? "Judicial"),
                    })
                  }
                  options={JURISDICTION_TYPE_OPTIONS}
                />
                <FormControl.SearchSelect
                  data-testid="SEL_governance_master_jurisdiction_enactment_law"
                  label="Enactment (Applicable Law)"
                  disabled={jurFieldsDisabled}
                  value={
                    masterForm.enactment_law_id
                      ? String(masterForm.enactment_law_id)
                      : ""
                  }
                  onChange={(value) =>
                    setMasterForm({
                      ...masterForm,
                      enactment_law_id: value || null,
                    })
                  }
                  options={lawOptions}
                  placeholder="— Select a Law —"
                />
                {enactmentLaws.length === 0 && (
                  <p style={{ fontSize: "0.75rem", color: "#94a3b8" }}>
                    No approved laws yet. Approve one under Law Library first.
                  </p>
                )}
                <ValidatedInput
                  vh={fieldVh}
                  name="location"
                  code={109}
                  disabled={jurFieldsDisabled}
                  data-testid="INP_governance_master_jurisdiction_location"
                  label="Location"
                  value={masterForm.location || ""}
                  onChange={(e) =>
                    setMasterForm({ ...masterForm, location: e.target.value })
                  }
                />
              </>
            )}

            {masterFormType === "provision" && (
              <>
                <ValidatedInput
                  vh={fieldVh}
                  name="section_reference"
                  code={109}
                  disabled={provisionFieldsDisabled}
                  data-testid="INP_governance_master_provision_section"
                  label="Section Reference"
                  placeholder="e.g. Section 135"
                  value={masterForm.section_reference || ""}
                  onChange={(e) =>
                    setMasterForm({
                      ...masterForm,
                      section_reference: e.target.value,
                    })
                  }
                />
                <ValidatedInput
                  vh={fieldVh}
                  name="title"
                  code={109}
                  required
                  disabled={provisionFieldsDisabled}
                  data-testid="INP_governance_master_provision_title"
                  label="Title"
                  value={masterForm.title || ""}
                  onChange={(e) =>
                    setMasterForm({ ...masterForm, title: e.target.value })
                  }
                />
                <ValidatedTextarea
                  vh={fieldVh}
                  name="description"
                  code={110}
                  disabled={provisionFieldsDisabled}
                  data-testid="INP_governance_master_provision_description"
                  label="Description"
                  rows={3}
                  value={masterForm.description || ""}
                  onChange={(e) =>
                    setMasterForm({
                      ...masterForm,
                      description: e.target.value,
                    })
                  }
                />
                <ValidatedTextarea
                  vh={fieldVh}
                  name="penalty_description"
                  code={110}
                  disabled={provisionFieldsDisabled}
                  data-testid="INP_governance_master_provision_penalty"
                  label="Penalty"
                  rows={2}
                  value={masterForm.penalty_description || ""}
                  onChange={(e) =>
                    setMasterForm({
                      ...masterForm,
                      penalty_description: e.target.value,
                    })
                  }
                />
              </>
            )}
          </form>
        </Modal.Body>
        {/* Provisions tab has its own "Create" button — no modal footer there */}
        {masterFormType === "law" && lawModalTab === "provisions" ? null : editingId ? (
          <Modal.Footer className="app-modal-footer--split">
            {!mcApproved && (
              <Button
                data-testid="BTN_governance_master_toggle_active"
                variant={
                  masterForm.is_active ? "inactive-outline" : "active-outline"
                }
                icon={masterForm.is_active ? CircleMinus : CircleCheck}
                onClick={handleToggleActive}
                disabled={toggling || submitting}
              >
                {toggling ? "..." : masterForm.is_active ? "Inactive" : "Active"}
              </Button>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              {mcForm || masterFormType === "provision" ? (
                mcApproved ? null : isFieldEditMode ? (
                  <Button
                    data-testid={`BTN_governance_master_${masterFormType}_update`}
                    variant="update"
                    icon={Check}
                    onClick={saveMaster}
                    disabled={submitting || toggling || permLoadingForType || !allowedForType}
                    title={!allowedForType ? NOT_AUTHORISED_MSG : undefined}
                  >
                    {submitting ? "Updating..." : "Update"}
                  </Button>
                ) : (
                  <Button
                    data-testid={`BTN_governance_master_${masterFormType}_edit`}
                    type="button"
                    variant="fill"
                    icon={Edit2}
                    onClick={() => setIsFieldEditMode(true)}
                    disabled={toggling || permLoadingForType || !allowedForType}
                    title={!allowedForType ? NOT_AUTHORISED_MSG : undefined}
                  >
                    Edit
                  </Button>
                )
              ) : (
                <Button
                  data-testid="BTN_governance_master_update"
                  variant="update"
                  icon={Check}
                  onClick={saveMaster}
                  disabled={submitting || toggling || (gateThis && (permLoadingForType || !allowedForType))}
                  title={gateThis && !allowedForType ? NOT_AUTHORISED_MSG : undefined}
                >
                  {submitting ? "Updating..." : "Update"}
                </Button>
              )}
            </div>
          </Modal.Footer>
        ) : (
          <Modal.Footer>
            <Button
              data-testid="BTN_governance_master_submit"
              variant="fill"
              icon={Save}
              onClick={saveMaster}
              disabled={submitting || (gateThis && (permLoadingForType || !allowedForType))}
              title={gateThis && !allowedForType ? NOT_AUTHORISED_MSG : undefined}
            >
              {submitting ? "Submitting..." : "Submit"}
            </Button>
          </Modal.Footer>
        )}
      </Modal>

      {/* ── Provision modal — stacks ABOVE the Law modal ───────────────── */}
      <Modal
        isOpen={showProvisionModal}
        onClose={() => setShowProvisionModal(false)}
        size="md"
      >
        <Modal.Header>
          {provisionEditingId
            ? (provisionFieldEdit ? <>Editing "<span className="item-highlight">{provisionForm.title || provisionForm.section_reference || "Provision"}</span>"</>
                                  : <>Viewing "<span className="item-highlight">{provisionForm.title || provisionForm.section_reference || "Provision"}</span>"</>)
            : "Create Provision"}
        </Modal.Header>
        <Modal.Body>
          <form
            data-testid="MOD_governance_master_provision_form"
            id="provision-form"
            onSubmit={saveProvision}
            className="flex flex-col gap-y-4"
          >
            <ValidatedInput
              vh={provisionVh}
              name="section_reference"
              code={109}
              disabled={provisionEditingId !== null && !provisionFieldEdit}
              data-testid="INP_governance_master_provision_section"
              label="Section Reference"
              placeholder="e.g. Section 135"
              value={provisionForm.section_reference || ""}
              onChange={(e) =>
                setProvisionForm({ ...provisionForm, section_reference: e.target.value })
              }
            />
            <ValidatedInput
              vh={provisionVh}
              name="title"
              code={109}
              required
              disabled={provisionEditingId !== null && !provisionFieldEdit}
              data-testid="INP_governance_master_provision_title"
              label="Title"
              value={provisionForm.title || ""}
              onChange={(e) =>
                setProvisionForm({ ...provisionForm, title: e.target.value })
              }
            />
            <ValidatedTextarea
              vh={provisionVh}
              name="description"
              code={110}
              disabled={provisionEditingId !== null && !provisionFieldEdit}
              data-testid="INP_governance_master_provision_description"
              label="Description"
              rows={3}
              value={provisionForm.description || ""}
              onChange={(e) =>
                setProvisionForm({ ...provisionForm, description: e.target.value })
              }
            />
            <ValidatedTextarea
              vh={provisionVh}
              name="penalty_description"
              code={110}
              disabled={provisionEditingId !== null && !provisionFieldEdit}
              data-testid="INP_governance_master_provision_penalty"
              label="Penalty"
              rows={2}
              value={provisionForm.penalty_description || ""}
              onChange={(e) =>
                setProvisionForm({ ...provisionForm, penalty_description: e.target.value })
              }
            />
          </form>
        </Modal.Body>
        {provisionEditingId ? (
          <Modal.Footer className="app-modal-footer--split">
            <Button
              data-testid="BTN_governance_master_provision_toggle_active"
              variant={provisionForm.is_active ? "inactive-outline" : "active-outline"}
              icon={provisionForm.is_active ? CircleMinus : CircleCheck}
              onClick={toggleProvisionActive}
              disabled={provisionSaving || !canModify}
            >
              {provisionSaving ? "..." : provisionForm.is_active ? "Inactive" : "Active"}
            </Button>
            <div style={{ display: "flex", gap: 8 }}>
              {provisionFieldEdit ? (
                <Button
                  data-testid="BTN_governance_master_provision_update"
                  variant="update"
                  icon={Check}
                  onClick={saveProvision}
                  disabled={provisionSaving || permLoading || !canModify}
                  title={!canModify ? NOT_AUTHORISED_MSG : undefined}
                >
                  {provisionSaving ? "Updating..." : "Update"}
                </Button>
              ) : (
                <Button
                  data-testid="BTN_governance_master_provision_edit"
                  type="button"
                  variant="fill"
                  icon={Edit2}
                  onClick={() => setProvisionFieldEdit(true)}
                  disabled={permLoading || !canModify}
                  title={!canModify ? NOT_AUTHORISED_MSG : undefined}
                >
                  Edit
                </Button>
              )}
            </div>
          </Modal.Footer>
        ) : (
          <Modal.Footer>
            <Button
              data-testid="BTN_governance_master_provision_submit"
              variant="fill"
              icon={Save}
              onClick={saveProvision}
              disabled={provisionSaving || permLoading || !canModify}
              title={!canModify ? NOT_AUTHORISED_MSG : undefined}
            >
              {provisionSaving ? "Submitting..." : "Submit"}
            </Button>
          </Modal.Footer>
        )}
      </Modal>

      {/* ── Jurisdiction workflow + history modal (maker-checker) ───────── */}
      <WorkflowModal
        isOpen={!!jurWfOpen}
        onClose={() => setJurWfOpen(null)}
        kind="gov_jurisdictions"
        id={jurWfOpen ? Number(jurWfOpen.id) : null}
        onChanged={loadJurisdictions}
      />

      {/* ── Party workflow + history modal (maker-checker) ──────────────── */}
      <WorkflowModal
        isOpen={!!partyWfOpen}
        onClose={() => setPartyWfOpen(null)}
        kind="gov_third_party"
        id={partyWfOpen ? Number(partyWfOpen.id) : null}
        onChanged={loadThirdParties}
      />

      {/* ── Applicable Law (Law Library) workflow + history modal (maker-checker) ── */}
      <WorkflowModal
        isOpen={!!lawWfOpen}
        onClose={() => setLawWfOpen(null)}
        kind="gov_applicable_laws"
        id={lawWfOpen ? Number(lawWfOpen.id) : null}
        onChanged={loadLaws}
      />
    </div>
  );
};

export default GovernanceMasters;
